import React, { useState } from 'react';

// Modal component for displaying educational content
const InfoModal = ({ isOpen, onClose, title, content, image }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4">
          {image && (
            <div className="mb-4">
              <img src={image} alt={title} className="w-full rounded-lg" />
            </div>
          )}
          <div className="text-gray-700">
            {content}
          </div>
        </div>
        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Info button component that can be placed next to questions
const InfoButton = ({ onClick, className = "" }) => (
  <button 
    onClick={onClick}
    className={`ml-1 text-blue-500 hover:text-blue-700 focus:outline-none ${className}`}
    aria-label="Learn more"
    title="Learn more"
  >
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
  </button>
);

// Educational content data
const infoContent = {
  showerhead: {
    title: "Water-Efficient Showerheads",
    content: (
      <>
        <p className="mb-3">
          Water-efficient showerheads use 5-7 LPM (liters per minute) compared to traditional showerheads which use about 10 LPM.
        </p>
        <p className="mb-3">
          Look for these features to identify if you have a water-efficient showerhead:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
       
          <li>Aerating function that mixes air with water</li>
          <li>Flow restrictor or regulator built in</li>
          <li>Multiple spray settings including "eco" or "water-saving" mode</li>
        </ul>
        <p>
          Switching to a water-efficient showerhead can save up to 18,000 liters of water per year for a family of four.
        </p>
      </>
    ),
    image: "/water-flow-rates.jpg" // Path to the image you provided
  },
  
  faucets: {
    title: "Water-Efficient Faucet Aerators",
    content: (
      <>
        <p className="mb-3">
          Efficient faucet aerators reduce water flow to 2-5 LPM compared to standard faucets that use 8 LPM.
        </p>
        <p className="mb-3">
          Ways to identify water-efficient faucets:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
        
          <li>Visible aerator attached to the faucet end</li>
          <li>Flow rate printed on the aerator </li>
          <li>Faucets with motion sensors or timed shut-off</li>
        </ul>
        <p>
          Installing efficient aerators is one of the most cost-effective water conservation measures available.
        </p>
      </>
    ),
    image: "/water-flow-rates.jpg" // Path to the image you provided
  },
  
  toilet: {
    title: "Efficient Toilet Types",
    content: (
      <>
        <p className="mb-3">
          Different toilet types use varying amounts of water per flush:
        </p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li><strong>Traditional Flush:</strong> Older toilets use 9-12 liters per flush</li>
          <li><strong>Standard Dual Flush:</strong> Typically 6 liters for full flush, 3-4 liters for half flush</li>
          <li><strong>Efficient Dual Flush:</strong> 4.5 liters or less for full flush, 3 liters or less for half flush</li>
        </ul>
        <p className="mb-3">
          How to identify your toilet type:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
        
          <li>Dual flush toilets have two buttons or a split flush lever</li>
       
        </ul>
        <p>
          Replacing an old toilet with a water-efficient model can save up to 30,000 liters of water per year for a family of four.
        </p>
      </>
    ),
    image: null
  },

  gardenWatering: {
    title: "Plant Watering Methods",
    content: (
      <>
        <p className="mb-3">
          Different watering methods use varying amounts of water:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
          <li><strong>Watering can/bucket:</strong> Around 10 liters per use, allowing precise application with minimal waste</li>
          <li><strong>Garden hose:</strong> Uses about 15 liters per minute with continuous flow</li>
          <li><strong>Hose with nozzle/sprinkler:</strong> Uses about 10 liters per minute with more controlled application</li>
        </ul>
        <p className="mb-3">
          Water-saving tips for potted plants:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
          <li>Water early morning or evening to reduce evaporation</li>
          <li>Use self-watering planters to reduce water waste</li>
          <li>Group plants with similar water needs together</li>
          <li>Add organic matter to soil to improve water retention</li>
          <li>Use a moisture meter to avoid overwatering</li>
        </ul>
      </>
    ),
    image: null
  },

  lawnWatering: {
    title: "Lawn Watering Systems",
    content: (
      <>
        <p className="mb-3">
          Watering systems differ in efficiency and water usage:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
          <li><strong>Standard hose:</strong> Uses about 15 liters per minute with less even coverage</li>
          <li><strong>Hose with nozzle/sprinkler:</strong> Uses about 10 liters per minute with better control</li>
          <li><strong>Automatic sprinkler system:</strong> Uses about 10 liters per minute with programmable timing</li>
          <li><strong>Drip irrigation:</strong> Most efficient at only 3 liters per minute, delivering water directly to roots</li>
        </ul>
        <p className="mb-3">
          For maximum lawn watering efficiency:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
          <li>Water deeply but infrequently to encourage deep root growth</li>
          <li>Early morning watering (5-10 AM) reduces evaporation by up to 30%</li>
          <li>Adjust watering schedule based on seasonal needs and rainfall</li>
          <li>Consider replacing part of your lawn with drought-resistant plants</li>
          <li>Use a rain sensor to automatically skip watering after rainfall</li>
        </ul>
      </>
    ),
    image: null
  },

  poolMaintenance: {
    title: "Swimming Pool Water Conservation",
    content: (
      <>
        <p className="mb-3">
          Swimming pools can lose significant water through evaporation, especially in hot climates.
        </p>
        <p className="mb-3">
          Benefits of using a pool cover:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
          <li>Reduces water evaporation by up to 95%</li>
        
          <li>Reduces energy costs for heated pools</li>
          <li>Keeps debris out, reducing cleaning time</li>
        </ul>
        <p className="mb-3">
          Additional pool water conservation tips:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
         
          <li>Check for and repair leaks regularly (a wet spot around the pool can indicate a leak)</li>
          <li>Maintain proper chemical balance to reduce need for water replacement</li>
          <li>Consider a cartridge filter that doesn't require backwashing</li>
          <li>Reuse backwash water for garden irrigation where appropriate</li>
        </ul>
        <p>
          A medium-sized uncovered pool in a hot climate can lose over 2,500 liters of water per month to evaporation.
        </p>
      </>
    ),
    image: null
  },

  carWashing: {
    title: "Water-Efficient Car Washing",
    content: (
      <>
        <p className="mb-3">
          Different car washing methods use varying amounts of water:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
          <li><strong>Standard hose:</strong> 150-300 liters per wash (15 liters per minute)</li>
          <li><strong>Hose with nozzle:</strong> 70-150 liters per wash (10 liters per minute)</li>
          <li><strong>Bucket and cloth:</strong> 15-40 liters per wash (using 1-2 buckets)</li>
          <li><strong>Commercial car wash:</strong> 60-150 liters per wash (many recycle water)</li>
        </ul>
        <p className="mb-3">
          Water-saving car washing tips:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
          <li>Wash your car on the lawn to water plants simultaneously</li>
          <li>Use a bucket with a sponge and a trigger nozzle hose for rinsing</li>
          <li>Consider waterless car wash products for light cleaning</li>
          <li>Wash your car during cooler parts of the day to reduce evaporation</li>
          <li>Collect rainwater for car washing</li>
        </ul>
      </>
    ),
    image: null
  },
  
  waterConservation: {
    title: "Outdoor Water Conservation Practices",
    content: (
      <>
        <p className="mb-3">
          Rainwater harvesting benefits:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
          <li>Reduces dependence on municipal water supply</li>
          <li>Provides chemical-free water ideal for plants</li>
          <li>Helps control stormwater runoff and flooding</li>
          <li>Simple systems can be installed with minimal investment</li>
          <li>A 1,000 sq. ft. roof can collect approximately 600 liters from 1 inch of rainfall</li>
        </ul>
        <p className="mb-3">
          Benefits of using mulch:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
          <li>Reduces water evaporation by up to 70%</li>
          <li>Suppresses weed growth that would compete for water</li>
          <li>Moderates soil temperature fluctuations</li>
          <li>Adds nutrients to soil as organic mulches decompose</li>
          <li>Prevents soil erosion and compaction</li>
        </ul>
        <p className="mb-3">
          Importance of checking for leaks:
        </p>
        <ul className="list-disc pl-5 mb-3 space-y-1">
          <li>Even small leaks can waste thousands of liters per month</li>
          <li>Common outdoor leak sources include hose connections, irrigation systems, and pool equipment</li>
          <li>A leak of just 1 drop per second wastes about 3,000 liters per year</li>
          <li>Regular inspection can identify problems before they become major</li>
        </ul>
      </>
    ),
    image: null
  }
};

export { InfoModal, InfoButton, infoContent };
